import React, { useCallback } from 'react';
import { FORM_ERROR } from 'final-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { setCollectionsRecords } from '../../data-layer/collections/actions';
import * as collectionsService from '../../data-layer/collections/service';
import { useDispatch } from '../../data-layer/useDispatch';
import { toCollectionPersistenceModel } from '../../mappers/collections/toCollectionPersistenceModel';
import { toCollectionPersistenceUI } from '../../mappers/collections/toCollectionPersistenceUI';
import { Dialog, DialogContent, DialogTitle } from '../../ui/feedback/Dialog';
import { Typography } from '../../ui/typography/Typography';
import { CollectionPersistenceForm } from '../../ui/users/CollectionPersistenceForm';
import { CollectionPersistenceUI } from '../../ui/users/types';
import { useEventSource } from '../../utils/LocationContext';
import { getPageNameByEventSource, ymTrackCollectionEdit } from '../../utils/metrika';
import { useTranslation } from '../../utils/useTranslation';
import { useCollection } from './useCollection';
import { useCurrentUser } from './useCurrentUser';

export function EditCollectionDialog() {
    const t = useTranslation();
    const dispatch = useDispatch();
    const { state, pathname } = useLocation();
    const navigate = useNavigate();
    const onNavigateBack = useCallback(() => {
        navigate(pathname);
    }, [navigate, pathname]);
    const { collectionModel } = useCollection({ collectionId: state?.collectionId });
    const initialValues = collectionModel ? toCollectionPersistenceUI(collectionModel) : undefined;
    const currentUser = useCurrentUser();
    const { eventSource } = useEventSource();

    const onSubmit = useCallback(
        async (collectionUI: CollectionPersistenceUI) => {
            try {
                const model = toCollectionPersistenceModel(collectionUI);
                const updatedModel = await collectionsService.updateCollection(model);

                dispatch(setCollectionsRecords(updatedModel));

                if (eventSource) {
                    ymTrackCollectionEdit({
                        page: getPageNameByEventSource(eventSource),
                        userLogin: currentUser?.login,
                        businessId: currentUser?.profile?.publicId,
                        isPublic: updatedModel.isPublic,
                        name: updatedModel.displayName,
                        collectionId: updatedModel.id,
                    });
                }

                onNavigateBack();
            } catch (error) {
                return { [FORM_ERROR]: 'Не удалось обновить коллекцию' };
            }
        },
        [dispatch, onNavigateBack, currentUser, eventSource],
    );

    return (
        <Dialog onClose={onNavigateBack}>
            <DialogTitle onClose={onNavigateBack}>
                <Typography variant="title3" weight="bold">
                    {t('Редактировать коллекцию')}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <CollectionPersistenceForm initialValues={initialValues} onSubmit={onSubmit} />
            </DialogContent>
        </Dialog>
    );
}
